import type { MpmNumericSettingChange } from "../mpm/mpmNumericSettingsChanges";
import type { AchievementId } from "@/features/achievements/types";
import {
  calculateNHovBranchContinuousLevel,
  calculateNHovBranchLevel,
} from "@/features/hoverIdle/nHov/nHovGrowthLogic";
import type {
  HoverIdleSnapshot,
  HovCellGainUpgradeIndex,
  MHovExecuteBeforeSnapshot,
  NHovBranchId,
} from "@/features/hoverIdle/types";
import { compareHuge, serializeHuge, type HugeNumber } from "@/shared/numbers";
import {
  buildPlayLogAmplifierSummary,
  buildPlayLogMpSummary,
  buildPlayLogWallSummary,
  type PlayLogHovSkillSummary,
} from "./playLogBalanceSummary";
import {
  buildPlayLogCompactBaseline,
  exportPlayLogCompactJsonLines,
} from "./playLogCompact";
import {
  createPlayLogRecorder,
  type PlayLogRecorder,
  type RecordPlayLogEventInput,
  type RecordPlayLogSampleInput,
  type StartPlayLogSessionInput,
  type StopPlayLogSessionInput,
} from "./playLogRecorder";
import { buildPlayLogSnapshotSummary } from "./playLogSnapshotSummary";
import type {
  PlayLogEntry,
  PlayLogEvent,
  PlayLogGameplayAutomationBurstSummary,
  PlayLogMHovExecuteSource,
  PlayLogNHovExecuteSource,
  PlayLogOHovExecuteSource,
  PlayLogResetPreviewProbePayload,
  PlayLogSample,
  PlayLogSession,
  PlayLogSessionEndEntry,
  PlayLogSnapshotSummary,
  PlayLogStatus,
} from "./playLogTypes";

export const PLAY_LOG_SAMPLE_INTERVAL_WALL_MS = 10_000;
export const PLAY_LOG_RESET_PREVIEW_PROBE_INTERVAL_GAME_MS = 10_000;
export const PLAY_LOG_RESET_PREVIEW_PROBE_EVENT_TYPE = "reset-preview-probe";

export type PlayLogTraceMode = "normal" | "analysis" | "calibration";

export type PlayLogHovGainUpgradeTarget =
  | "global"
  | { readonly kind: "cell"; readonly index: HovCellGainUpgradeIndex };

export interface PlayLogHovGainUpgradePurchase {
  readonly target: PlayLogHovGainUpgradeTarget;
  readonly isMilestone: boolean;
  readonly enhancementLevelBefore: number;
  readonly enhancementLevelAfter?: number;
}

export interface CreatePlayLogRuntimeOptions {
  readonly runId?: string;
  readonly nowMs?: () => number;
  readonly random?: () => number;
  readonly getSnapshot: () => HoverIdleSnapshot;
  readonly getSpeedMultiplier: () => number;
  readonly recorder?: PlayLogRecorder;
  readonly sampleIntervalWallMs?: number;
  readonly resetPreviewProbeIntervalGameMs?: number;
  // Caller-side capability gate. Enables calibration traces (such as
  // reset-preview-probe) when the session purpose does not include
  // "calibration". Intended for admin-accessible runtimes. Audience and
  // player-facing boundaries are unchanged.
  readonly canRecordCalibrationTrace?: boolean;
  // Optional getter for the current Hov skill summary. Hov skill state lives
  // outside `HoverIdleSnapshot`, so callers that want acquired-skill / resolved
  // skill effect data on snapshot summaries and reset event payloads must
  // provide it. Returning `undefined` leaves the field off the snapshot
  // summary; useful for tests that do not exercise the skill model.
  readonly getHovSkillSummary?: () => PlayLogHovSkillSummary | undefined;
}

export interface PlayLogRuntimeStartSessionInput extends Partial<StartPlayLogSessionInput> {}

export interface PlayLogTimeAdvanceInput {
  readonly wallDeltaMs: number;
  readonly gameDeltaMs?: number;
  readonly speedMultiplier?: number;
}

export interface PlayLogRuntimeEventOptions {
  readonly payload?: Record<string, unknown>;
  readonly snapshot?: HoverIdleSnapshot;
}

export interface PlayLogOfflineRewardApplyInput {
  readonly source: string;
  readonly realElapsedMs: number;
  readonly uncappedGameElapsedMs: number;
  readonly gameElapsedMs: number;
  readonly wasCapped: boolean;
  readonly rewardId: string;
  readonly rewardFormulaVersion: string;
  readonly timGained: string;
  readonly resultDigest: string;
  readonly commitRevision: number;
  readonly inputPreparationMs: number;
  readonly mainThreadSingleTaskMs: number;
  readonly mainThreadTotalBlockingMs: number;
  readonly endToEndMs: number;
  readonly snapshot?: HoverIdleSnapshot;
}

export interface PlayLogRuntime {
  startSession(input?: PlayLogRuntimeStartSessionInput): PlayLogSession;
  stopSession(
    input?: Partial<StopPlayLogSessionInput>,
  ): PlayLogSessionEndEntry | null;
  clear(): void;
  getStatus(): PlayLogStatus;
  advanceTime(input: PlayLogTimeAdvanceInput): void;
  recordDueSample(snapshot?: HoverIdleSnapshot): PlayLogSample | null;
  recordDueResetPreviewProbe(snapshot?: HoverIdleSnapshot): PlayLogEvent | null;
  recordEvent(
    type: string,
    options?: PlayLogRuntimeEventOptions,
  ): PlayLogEvent | null;
  recordMpmNumericSettingsChange(
    changes: readonly MpmNumericSettingChange[],
  ): PlayLogEvent | null;
  recordSpeedChange(input: {
    readonly from: number;
    readonly to: number;
    readonly snapshot?: HoverIdleSnapshot;
  }): PlayLogEvent | null;
  recordAchievementUnlock(
    ids: readonly AchievementId[],
    snapshot?: HoverIdleSnapshot,
  ): PlayLogEvent | null;
  recordHovGainUpgradePurchase(input: {
    readonly target: PlayLogHovGainUpgradeTarget;
    readonly isMilestone: boolean;
    readonly enhancementLevelBefore: number;
    readonly enhancementLevelAfter?: number;
    readonly snapshot?: HoverIdleSnapshot;
  }): PlayLogEvent | null;
  recordHovGainUpgradePurchaseBatch(input: {
    readonly purchases: readonly PlayLogHovGainUpgradePurchase[];
    readonly snapshot?: HoverIdleSnapshot;
  }): PlayLogEvent | null;
  recordGameplayAutomationBurst(input: {
    readonly summary: PlayLogGameplayAutomationBurstSummary;
    readonly snapshot?: HoverIdleSnapshot;
  }): PlayLogEvent | null;
  recordMHovExecute(input: {
    readonly ok: boolean;
    readonly executionSource?: PlayLogMHovExecuteSource;
    readonly beforeSnapshot?: MHovExecuteBeforeSnapshot;
    readonly snapshot?: HoverIdleSnapshot;
  }): PlayLogEvent | null;
  recordNHovExecute(input: {
    readonly branchId: NHovBranchId;
    readonly ok: boolean;
    readonly executionSource?: PlayLogNHovExecuteSource;
    readonly beforeSnapshot?: HoverIdleSnapshot;
    readonly snapshot?: HoverIdleSnapshot;
  }): PlayLogEvent | null;
  recordOHovExecute(input: {
    readonly ok: boolean;
    readonly executionSource?: PlayLogOHovExecuteSource;
    readonly beforeSnapshot?: HoverIdleSnapshot;
    readonly snapshot?: HoverIdleSnapshot;
  }): PlayLogEvent | null;
  recordOHovAutoExecute(input: {
    readonly source: string;
    readonly executionSource?: PlayLogOHovExecuteSource;
    readonly beforeSnapshot?: HoverIdleSnapshot;
    readonly snapshot?: HoverIdleSnapshot;
  }): PlayLogEvent | null;
  recordOfflineRewardApply(
    input: PlayLogOfflineRewardApplyInput,
  ): PlayLogEvent | null;
  exportPlayLogJsonLines(): string;
  exportPlayLogCompactJsonLines(): string;
  getEntries(): readonly PlayLogEntry[];
  getRecorder(): PlayLogRecorder;
}

export function createPlayLogRuntime(
  options: CreatePlayLogRuntimeOptions,
): PlayLogRuntime {
  return new DefaultPlayLogRuntime(options);
}

class DefaultPlayLogRuntime implements PlayLogRuntime {
  private readonly nowMs: () => number;
  private readonly getSnapshot: () => HoverIdleSnapshot;
  private readonly getSpeedMultiplier: () => number;
  private readonly recorder: PlayLogRecorder;
  private readonly sampleIntervalWallMs: number;
  private readonly resetPreviewProbeIntervalGameMs: number;
  private readonly canRecordCalibrationTrace: boolean;
  private readonly getHovSkillSummary?: () =>
    | PlayLogHovSkillSummary
    | undefined;
  private wallTimeMs = 0;
  private gameTimeMs = 0;
  private speedMultiplier = 1;
  private nextSampleWallTimeMs: number;
  private nextResetPreviewProbeGameTimeMs: number;
  private previousResetPreviewProbe: PreviousResetPreviewProbeState | undefined;
  private traceMode: PlayLogTraceMode = "normal";
  private sessionStartedAtMs: number | null = null;
  // Snapshot summary captured at session start so compact PlayLog can emit a
  // baseline header. Detailed PlayLog is unaffected; baseline is compact-only.
  private sessionBaselineSnapshot: PlayLogSnapshotSummary | null = null;
  private started = false;
  private stopped = false;

  constructor(options: CreatePlayLogRuntimeOptions) {
    this.nowMs = options.nowMs ?? Date.now;
    this.getSnapshot = options.getSnapshot;
    this.getSpeedMultiplier = options.getSpeedMultiplier;
    this.sampleIntervalWallMs = normalizePositiveFinite(
      options.sampleIntervalWallMs ?? PLAY_LOG_SAMPLE_INTERVAL_WALL_MS,
      "sampleIntervalWallMs",
    );
    this.resetPreviewProbeIntervalGameMs = normalizePositiveFinite(
      options.resetPreviewProbeIntervalGameMs ??
        PLAY_LOG_RESET_PREVIEW_PROBE_INTERVAL_GAME_MS,
      "resetPreviewProbeIntervalGameMs",
    );
    this.nextSampleWallTimeMs = this.sampleIntervalWallMs;
    this.nextResetPreviewProbeGameTimeMs = this.resetPreviewProbeIntervalGameMs;
    this.canRecordCalibrationTrace = options.canRecordCalibrationTrace ?? false;
    if (options.getHovSkillSummary !== undefined) {
      this.getHovSkillSummary = options.getHovSkillSummary;
    }
    const runId =
      options.runId ??
      createDefaultPlayLogRunId(this.nowMs(), options.random ?? Math.random);
    this.recorder = options.recorder ?? createPlayLogRecorder({ runId });
  }

  startSession(input: PlayLogRuntimeStartSessionInput = {}): PlayLogSession {
    if (this.started && !this.stopped) {
      const current = this.recorder.getSession();
      if (current === undefined) {
        throw new Error("PlayLog runtime session state is inconsistent.");
      }
      return current;
    }
    if (this.started && this.stopped) {
      this.clear();
    }

    this.wallTimeMs = 0;
    this.gameTimeMs = 0;
    this.speedMultiplier = normalizeNonNegativeFinite(
      input.initialSpeedMultiplier ?? this.getSpeedMultiplier(),
      "initialSpeedMultiplier",
    );
    this.nextSampleWallTimeMs = this.sampleIntervalWallMs;
    this.nextResetPreviewProbeGameTimeMs = this.resetPreviewProbeIntervalGameMs;
    this.previousResetPreviewProbe = undefined;
    this.traceMode = resolvePlayLogTraceMode(input.purpose);

    const startedAtMs = input.startedAtMs ?? this.nowMs();
    const session = this.recorder.startSession({
      startedAtMs,
      initialSpeedMultiplier: this.speedMultiplier,
      ...(input.label !== undefined ? { label: input.label } : {}),
      ...(input.purpose !== undefined ? { purpose: input.purpose } : {}),
    });
    this.sessionStartedAtMs = startedAtMs;
    this.sessionBaselineSnapshot = this.buildSnapshotSummary(
      this.getSnapshot(),
    );
    this.started = true;
    this.stopped = false;

    return session;
  }

  stopSession(
    input: Partial<StopPlayLogSessionInput> = {},
  ): PlayLogSessionEndEntry | null {
    if (!this.started) {
      return null;
    }
    if (this.stopped) {
      const current = this.recorder.getSessionEnd();
      if (current === undefined) {
        throw new Error("PlayLog runtime session end state is inconsistent.");
      }
      return current;
    }
    this.ensureStarted();

    const sessionEnd = this.recorder.stopSession({
      endedAtMs: input.endedAtMs ?? this.nowMs(),
    });
    this.stopped = true;

    return sessionEnd;
  }

  clear(): void {
    this.recorder.clear();
    this.wallTimeMs = 0;
    this.gameTimeMs = 0;
    this.speedMultiplier = normalizeNonNegativeFinite(
      this.getSpeedMultiplier(),
      "speedMultiplier",
    );
    this.nextSampleWallTimeMs = this.sampleIntervalWallMs;
    this.nextResetPreviewProbeGameTimeMs = this.resetPreviewProbeIntervalGameMs;
    this.previousResetPreviewProbe = undefined;
    this.traceMode = "normal";
    this.sessionStartedAtMs = null;
    this.sessionBaselineSnapshot = null;
    this.started = false;
    this.stopped = false;
  }

  getStatus(): PlayLogStatus {
    const session = this.recorder.getSession();
    const sessionEnd = this.recorder.getSessionEnd();
    const speedMultiplier = this.isRecording()
      ? this.speedMultiplier
      : normalizeNonNegativeFinite(
          this.getSpeedMultiplier(),
          "speedMultiplier",
        );

    return {
      isRecording: this.isRecording(),
      runId: session?.runId ?? null,
      entriesCount: this.recorder.getEntries().length,
      ...(session !== undefined ? { startedAtMs: session.startedAtMs } : {}),
      ...(sessionEnd !== undefined ? { endedAtMs: sessionEnd.endedAtMs } : {}),
      speedMultiplier,
    };
  }

  advanceTime(input: PlayLogTimeAdvanceInput): void {
    if (!this.isRecording()) {
      return;
    }

    const speedMultiplier = normalizeNonNegativeFinite(
      input.speedMultiplier ?? this.getSpeedMultiplier(),
      "speedMultiplier",
    );
    const wallDeltaMs = normalizeNonNegativeFinite(
      input.wallDeltaMs,
      "wallDeltaMs",
    );
    const gameDeltaMs = normalizeNonNegativeFinite(
      input.gameDeltaMs ?? wallDeltaMs * speedMultiplier,
      "gameDeltaMs",
    );

    this.wallTimeMs += wallDeltaMs;
    this.gameTimeMs += gameDeltaMs;
    this.speedMultiplier = speedMultiplier;
  }

  recordDueSample(snapshot?: HoverIdleSnapshot): PlayLogSample | null {
    if (!this.isRecording()) {
      return null;
    }
    this.syncWallTimeFromClock();

    if (this.wallTimeMs < this.nextSampleWallTimeMs) {
      return null;
    }

    const sample = this.recorder.recordSample(
      this.withCurrentTime({
        snapshot: this.buildSnapshotSummary(snapshot ?? this.getSnapshot()),
      }),
    );
    this.nextSampleWallTimeMs =
      Math.floor(this.wallTimeMs / this.sampleIntervalWallMs + 1) *
      this.sampleIntervalWallMs;

    return sample;
  }

  recordDueResetPreviewProbe(
    snapshot?: HoverIdleSnapshot,
  ): PlayLogEvent | null {
    if (!this.isRecording() || !this.isCalibrationTraceEnabled()) {
      return null;
    }
    this.syncWallTimeFromClock();

    if (this.gameTimeMs < this.nextResetPreviewProbeGameTimeMs) {
      return null;
    }

    const payload = buildResetPreviewProbePayload({
      snapshot: snapshot ?? this.getSnapshot(),
      gameTimeMs: this.gameTimeMs,
      probeIntervalGameMs: this.resetPreviewProbeIntervalGameMs,
      previous: this.previousResetPreviewProbe,
    });
    const event = this.recorder.recordEvent(
      this.withCurrentTime({
        type: PLAY_LOG_RESET_PREVIEW_PROBE_EVENT_TYPE,
        payload: payload as unknown as Record<string, unknown>,
      }),
    );
    this.previousResetPreviewProbe = toPreviousResetPreviewProbeState(
      payload,
      this.gameTimeMs,
    );
    this.nextResetPreviewProbeGameTimeMs =
      Math.floor(this.gameTimeMs / this.resetPreviewProbeIntervalGameMs + 1) *
      this.resetPreviewProbeIntervalGameMs;

    return event;
  }

  recordEvent(
    type: string,
    options: PlayLogRuntimeEventOptions = {},
  ): PlayLogEvent | null {
    if (!this.isRecording()) {
      return null;
    }
    this.syncWallTimeFromClock();

    return this.recorder.recordEvent(
      this.withCurrentTime({
        type,
        ...(options.payload !== undefined ? { payload: options.payload } : {}),
        snapshot: this.buildSnapshotSummary(
          options.snapshot ?? this.getSnapshot(),
        ),
      }),
    );
  }

  recordMpmNumericSettingsChange(
    changes: readonly MpmNumericSettingChange[],
  ): PlayLogEvent | null {
    if (!this.isRecording() || changes.length === 0) return null;
    return this.recordEvent("mpm-numeric-settings-change", {
      payload: {
        changes: changes.map((change) => ({
          ...change,
          before:
            typeof change.before === "number"
              ? change.before
              : serializeHuge(change.before),
          after:
            typeof change.after === "number"
              ? change.after
              : serializeHuge(change.after),
        })),
      },
    });
  }

  recordSpeedChange(input: {
    readonly from: number;
    readonly to: number;
    readonly snapshot?: HoverIdleSnapshot;
  }): PlayLogEvent | null {
    if (input.from === input.to) {
      return null;
    }
    this.speedMultiplier = normalizeNonNegativeFinite(
      input.to,
      "speedMultiplier",
    );

    return this.recordEvent("speed-change", {
      payload: {
        from: input.from,
        to: input.to,
      },
      snapshot: input.snapshot,
    });
  }

  recordAchievementUnlock(
    ids: readonly AchievementId[],
    snapshot?: HoverIdleSnapshot,
  ): PlayLogEvent | null {
    if (ids.length === 0) {
      return null;
    }

    return this.recordEvent("achievement-unlock", {
      payload: { ids: [...ids] },
      snapshot,
    });
  }

  recordHovGainUpgradePurchase(input: {
    readonly target: PlayLogHovGainUpgradeTarget;
    readonly isMilestone: boolean;
    readonly enhancementLevelBefore: number;
    readonly enhancementLevelAfter?: number;
    readonly snapshot?: HoverIdleSnapshot;
  }): PlayLogEvent | null {
    return this.recordHovGainUpgradePurchaseBatch({
      purchases: [input],
      snapshot: input.snapshot,
    });
  }

  recordHovGainUpgradePurchaseBatch(input: {
    readonly purchases: readonly PlayLogHovGainUpgradePurchase[];
    readonly snapshot?: HoverIdleSnapshot;
  }): PlayLogEvent | null {
    const milestonePurchases = input.purchases.filter((purchase) => {
      return purchase.isMilestone;
    });
    if (milestonePurchases.length === 0) {
      return null;
    }

    if (milestonePurchases.length === 1) {
      const purchase = milestonePurchases[0]!;
      return this.recordEvent("upgrade-milestone-purchase", {
        payload: buildHovGainUpgradePurchasePayload(purchase),
        snapshot: input.snapshot,
      });
    }

    return this.recordEvent("upgrade-milestone-purchase-batch", {
      payload: {
        purchases: milestonePurchases.map((purchase) =>
          buildHovGainUpgradePurchasePayload(purchase),
        ),
      },
      snapshot: input.snapshot,
    });
  }

  recordGameplayAutomationBurst(input: {
    readonly summary: PlayLogGameplayAutomationBurstSummary;
    readonly snapshot?: HoverIdleSnapshot;
  }): PlayLogEvent | null {
    return this.recordEvent("gameplay-automation-burst-summary", {
      payload: {
        ...input.summary,
        nHovCountsByBranch: { ...input.summary.nHovCountsByBranch },
        nHovBranchLevelGains: { ...input.summary.nHovBranchLevelGains },
        unlockedTargetIndexes: [...input.summary.unlockedTargetIndexes],
        orderedEvents: input.summary.orderedEvents.map((event) => ({
          ...event,
        })),
      },
      snapshot: input.snapshot,
    });
  }

  recordMHovExecute(input: {
    readonly ok: boolean;
    readonly executionSource?: PlayLogMHovExecuteSource;
    readonly beforeSnapshot?: MHovExecuteBeforeSnapshot;
    readonly snapshot?: HoverIdleSnapshot;
  }): PlayLogEvent | null {
    if (!this.isRecording()) {
      return null;
    }
    const snapshot = input.snapshot ?? this.getSnapshot();
    const beforeSnapshot = input.beforeSnapshot ?? snapshot;
    return this.recordEvent("mhov-execute", {
      payload: {
        ok: input.ok,
        executionSource: input.executionSource ?? "unknown",
        ...buildMHovExecutePayload(beforeSnapshot, snapshot),
      },
      snapshot,
    });
  }

  recordNHovExecute(input: {
    readonly branchId: NHovBranchId;
    readonly ok: boolean;
    readonly executionSource?: PlayLogNHovExecuteSource;
    readonly beforeSnapshot?: HoverIdleSnapshot;
    readonly snapshot?: HoverIdleSnapshot;
  }): PlayLogEvent | null {
    if (!this.isRecording()) {
      return null;
    }
    const snapshot = input.snapshot ?? this.getSnapshot();
    const beforeSnapshot = input.beforeSnapshot ?? snapshot;
    return this.recordEvent("nhov-execute", {
      payload: {
        branchId: input.branchId,
        ok: input.ok,
        executionSource: input.executionSource ?? "unknown",
        ...buildNHovExecutePayload(input.branchId, beforeSnapshot, snapshot),
        ...this.buildNHovBeforeBalanceState(beforeSnapshot),
      },
      snapshot,
    });
  }

  recordOHovExecute(input: {
    readonly ok: boolean;
    readonly executionSource?: PlayLogOHovExecuteSource;
    readonly beforeSnapshot?: HoverIdleSnapshot;
    readonly snapshot?: HoverIdleSnapshot;
  }): PlayLogEvent | null {
    if (!this.isRecording()) {
      return null;
    }
    const snapshot = input.snapshot ?? this.getSnapshot();
    const beforeSnapshot = input.beforeSnapshot ?? snapshot;
    return this.recordEvent("ohov-execute", {
      payload: {
        ok: input.ok,
        executionSource: input.executionSource ?? "manual",
        ...buildOHovExecutePayload(beforeSnapshot, snapshot),
        ...this.buildOHovBeforeBalanceState(beforeSnapshot),
      },
      snapshot,
    });
  }

  recordOHovAutoExecute(input: {
    readonly source: string;
    readonly executionSource?: PlayLogOHovExecuteSource;
    readonly beforeSnapshot?: HoverIdleSnapshot;
    readonly snapshot?: HoverIdleSnapshot;
  }): PlayLogEvent | null {
    if (!this.isRecording()) {
      return null;
    }
    const snapshot = input.snapshot ?? this.getSnapshot();
    const beforeSnapshot = input.beforeSnapshot ?? snapshot;
    return this.recordEvent("ohov-auto-execute", {
      payload: {
        source: input.source,
        executionSource: input.executionSource ?? "auto",
        ...buildOHovExecutePayload(beforeSnapshot, snapshot),
        ...this.buildOHovBeforeBalanceState(beforeSnapshot),
      },
      snapshot,
    });
  }

  recordOfflineRewardApply(
    input: PlayLogOfflineRewardApplyInput,
  ): PlayLogEvent | null {
    return this.recordEvent("offline-reward-apply", {
      payload: {
        source: input.source,
        realElapsedMs: input.realElapsedMs,
        uncappedGameElapsedMs: input.uncappedGameElapsedMs,
        gameElapsedMs: input.gameElapsedMs,
        wasCapped: input.wasCapped,
        rewardId: input.rewardId,
        rewardFormulaVersion: input.rewardFormulaVersion,
        timGained: input.timGained,
        resultDigest: input.resultDigest,
        commitRevision: input.commitRevision,
        inputPreparationMs: input.inputPreparationMs,
        mainThreadSingleTaskMs: input.mainThreadSingleTaskMs,
        mainThreadTotalBlockingMs: input.mainThreadTotalBlockingMs,
        endToEndMs: input.endToEndMs,
      },
      snapshot: input.snapshot,
    });
  }

  exportPlayLogJsonLines(): string {
    return this.recorder.exportJsonLines();
  }

  exportPlayLogCompactJsonLines(): string {
    const session = this.recorder.getSession();
    if (session === undefined) {
      return "";
    }
    const sessionEnd = this.recorder.getSessionEnd();
    const baselineSnapshot = this.sessionBaselineSnapshot;
    const baseline =
      baselineSnapshot === null
        ? undefined
        : buildPlayLogCompactBaseline({ snapshot: baselineSnapshot });
    return exportPlayLogCompactJsonLines({
      session,
      entries: this.recorder.getEntries(),
      ...(sessionEnd !== undefined ? { sessionEnd } : {}),
      ...(baseline !== undefined ? { baseline } : {}),
    });
  }

  getEntries(): readonly PlayLogEntry[] {
    return this.recorder.getEntries();
  }

  getRecorder(): PlayLogRecorder {
    return this.recorder;
  }

  private withCurrentTime(
    input: Omit<
      RecordPlayLogEventInput,
      "wallTimeMs" | "gameTimeMs" | "speedMultiplier"
    >,
  ): RecordPlayLogEventInput;
  private withCurrentTime(
    input: Omit<
      RecordPlayLogSampleInput,
      "wallTimeMs" | "gameTimeMs" | "speedMultiplier"
    >,
  ): RecordPlayLogSampleInput;
  private withCurrentTime(
    input:
      | Omit<
          RecordPlayLogEventInput,
          "wallTimeMs" | "gameTimeMs" | "speedMultiplier"
        >
      | Omit<
          RecordPlayLogSampleInput,
          "wallTimeMs" | "gameTimeMs" | "speedMultiplier"
        >,
  ): RecordPlayLogEventInput | RecordPlayLogSampleInput {
    return {
      ...input,
      wallTimeMs: this.wallTimeMs,
      gameTimeMs: this.gameTimeMs,
      speedMultiplier: this.speedMultiplier,
    };
  }

  private ensureStarted(): void {
    if (!this.started) {
      throw new Error("PlayLog runtime session has not started.");
    }
  }

  private isRecording(): boolean {
    return this.started && !this.stopped;
  }

  private isCalibrationTraceEnabled(): boolean {
    return this.traceMode === "calibration" || this.canRecordCalibrationTrace;
  }

  private syncWallTimeFromClock(): void {
    if (this.sessionStartedAtMs === null) {
      return;
    }
    const elapsedMs = this.nowMs() - this.sessionStartedAtMs;
    if (Number.isFinite(elapsedMs) && elapsedMs > this.wallTimeMs) {
      this.wallTimeMs = elapsedMs;
    }
  }

  private buildSnapshotSummary(snapshot: HoverIdleSnapshot) {
    const skillSummary = this.getHovSkillSummary?.();
    return buildPlayLogSnapshotSummary(snapshot, {
      ...(skillSummary !== undefined ? { hovSkillSummary: skillSummary } : {}),
    });
  }

  private buildOHovBeforeBalanceState(
    beforeSnapshot: HoverIdleSnapshot,
  ): Record<string, unknown> {
    return {
      ...(this.getHovSkillSummary !== undefined
        ? maybeHovSkillSummary("hovSkillBefore", this.getHovSkillSummary())
        : {}),
      amplifierBefore: buildPlayLogAmplifierSummary(
        beforeSnapshot.hovAmplifier,
      ),
      wallBefore: buildPlayLogWallSummary(beforeSnapshot.wall),
      mpBefore: buildPlayLogMpSummary(beforeSnapshot),
      nHovBranchLevelsBefore: { ...beforeSnapshot.nHov.derivedBranchLevels },
      mHovCountBefore: beforeSnapshot.mHov.count,
      ...toHugePayload(
        "mHovMultiplierBefore",
        beforeSnapshot.mHov.hgMultiplier,
      ),
      nHovCountBefore: beforeSnapshot.nHov.count,
      hgEnhancementLevelBefore: beforeSnapshot.hovUpgradeEnhancementLevel,
    };
  }

  private buildNHovBeforeBalanceState(
    beforeSnapshot: HoverIdleSnapshot,
  ): Record<string, unknown> {
    return {
      ...(this.getHovSkillSummary !== undefined
        ? maybeHovSkillSummary("hovSkillBefore", this.getHovSkillSummary())
        : {}),
      mpBefore: buildPlayLogMpSummary(beforeSnapshot),
      mHovCountBefore: beforeSnapshot.mHov.count,
      ...toHugePayload(
        "mHovMultiplierBefore",
        beforeSnapshot.mHov.hgMultiplier,
      ),
      hgEnhancementLevelBefore: beforeSnapshot.hovUpgradeEnhancementLevel,
      nHovBranchLevelsBefore: { ...beforeSnapshot.nHov.derivedBranchLevels },
    };
  }
}

function maybeHovSkillSummary(
  fieldName: string,
  summary: PlayLogHovSkillSummary | undefined,
): Record<string, PlayLogHovSkillSummary> {
  if (summary === undefined) return {};
  return { [fieldName]: summary };
}

interface PreviousResetPreviewProbeState {
  readonly gameTimeMs: number;
  readonly mHov: {
    readonly previewRatioLog10: number | null;
    readonly previewMultiplierAfterLog10: number | null;
    readonly previewRewardBasisHovLog10: number | null;
  };
  readonly nHovNextLevelProgressByBranchId: Partial<
    Record<NHovBranchId, number>
  >;
}

interface BuildResetPreviewProbePayloadInput {
  readonly snapshot: HoverIdleSnapshot;
  readonly gameTimeMs: number;
  readonly probeIntervalGameMs: number;
  readonly previous?: PreviousResetPreviewProbeState;
}

function buildResetPreviewProbePayload(
  input: BuildResetPreviewProbePayloadInput,
): PlayLogResetPreviewProbePayload {
  const secondsSincePreviousProbe =
    input.previous === undefined
      ? null
      : (input.gameTimeMs - input.previous.gameTimeMs) / 1000;
  return {
    kind: "reset-preview-probe",
    audience: "simulation",
    probeIntervalGameMs: input.probeIntervalGameMs,
    secondsSincePreviousProbe,
    mHov: buildResetPreviewProbeMHov(input.snapshot, {
      secondsSincePreviousProbe,
      previous: input.previous,
    }),
    nHov: buildResetPreviewProbeNHov(input.snapshot, input.previous),
  };
}

function buildResetPreviewProbeMHov(
  snapshot: HoverIdleSnapshot,
  context: {
    readonly secondsSincePreviousProbe: number | null;
    readonly previous?: PreviousResetPreviewProbeState;
  },
): PlayLogResetPreviewProbePayload["mHov"] {
  const previewMultiplierAfterLog10 = toSafeLog10(
    snapshot.mHov.effectivePendingMultiplier,
  );
  const multiplierBeforeLog10 = toSafeLog10(snapshot.mHov.hgMultiplier);
  const previewRatioLog10 =
    previewMultiplierAfterLog10 !== null && multiplierBeforeLog10 !== null
      ? previewMultiplierAfterLog10 - multiplierBeforeLog10
      : null;
  const previewRewardBasisHovLog10 = toSafeLog10(
    snapshot.mHov.currentLoopHovEarned,
  );
  const previous = context.previous?.mHov;

  return {
    canExecute: snapshot.mHov.canExecute,
    previewRatioLog10,
    previewMultiplierAfterLog10,
    previewRewardBasisHovLog10,
    secondsSincePreviousProbe: context.secondsSincePreviousProbe,
    previewRatioLog10DeltaSincePreviousProbe: subtractNullable(
      previewRatioLog10,
      previous?.previewRatioLog10 ?? null,
    ),
    previewMultiplierAfterLog10DeltaSincePreviousProbe: subtractNullable(
      previewMultiplierAfterLog10,
      previous?.previewMultiplierAfterLog10 ?? null,
    ),
    previewRewardBasisHovLog10DeltaSincePreviousProbe: subtractNullable(
      previewRewardBasisHovLog10,
      previous?.previewRewardBasisHovLog10 ?? null,
    ),
  };
}

function buildResetPreviewProbeNHov(
  snapshot: HoverIdleSnapshot,
  previous: PreviousResetPreviewProbeState | undefined,
): PlayLogResetPreviewProbePayload["nHov"] {
  const visiblePreviews = snapshot.nHov.rewardPreviews.filter(
    (preview) => preview.isAvailable,
  );

  return {
    visibleCandidateBranchIds: visiblePreviews.map(
      (preview) => preview.branchId,
    ),
    candidatePreviews: visiblePreviews.map((preview) => {
      const branchId = preview.branchId;
      const experienceAfter = preview.experienceAfter;
      const prospectiveLevelAfter = preview.freshnessWallActive
        ? calculateNHovBranchContinuousLevel({ experience: experienceAfter })
        : calculateNHovBranchLevel({ experience: experienceAfter });
      const levelBefore = snapshot.nHov.derivedBranchLevels[branchId];
      const levelAfter = preview.canExecute
        ? prospectiveLevelAfter
        : levelBefore;
      const nextLevelProgress = normalizeRatio(
        preview.branchLevelProgressRatio,
      );

      return {
        branchId,
        canExecute: preview.canExecute,
        experienceGainedLog10: toSafeLog10(preview.experienceGain),
        levelBefore,
        levelAfter,
        levelGain: levelAfter - levelBefore,
        experienceAfterLog10: toSafeLog10(experienceAfter),
        effectBeforeLog10: toSafeLog10(preview.currentEffect),
        freshEffectAfterLog10: toSafeLog10(preview.freshNextEffect),
        effectiveEffectAfterLog10: toSafeLog10(preview.effectiveNextEffect),
        nextLevelProgress,
        nextLevelProgressDeltaSincePreviousProbe: subtractNullable(
          nextLevelProgress,
          previous?.nHovNextLevelProgressByBranchId[branchId] ?? null,
        ),
      };
    }),
  };
}

function toPreviousResetPreviewProbeState(
  payload: PlayLogResetPreviewProbePayload,
  gameTimeMs: number,
): PreviousResetPreviewProbeState {
  const nHovNextLevelProgressByBranchId: Partial<Record<NHovBranchId, number>> =
    {};
  for (const preview of payload.nHov.candidatePreviews) {
    nHovNextLevelProgressByBranchId[preview.branchId] =
      preview.nextLevelProgress;
  }

  return {
    gameTimeMs,
    mHov: {
      previewRatioLog10: payload.mHov.previewRatioLog10,
      previewMultiplierAfterLog10: payload.mHov.previewMultiplierAfterLog10,
      previewRewardBasisHovLog10: payload.mHov.previewRewardBasisHovLog10,
    },
    nHovNextLevelProgressByBranchId,
  };
}

function createDefaultPlayLogRunId(
  startedAtMs: number,
  random: () => number,
): string {
  const randomPart = Math.floor(Math.max(0, random()) * 1_000_000_000)
    .toString(36)
    .padStart(6, "0");
  return `playlog-${Math.floor(startedAtMs)}-${randomPart}`;
}

function cloneUpgradeTarget(
  target: PlayLogHovGainUpgradeTarget,
): PlayLogHovGainUpgradeTarget {
  if (target === "global") {
    return target;
  }

  return { ...target };
}

function buildHovGainUpgradePurchasePayload(
  purchase: PlayLogHovGainUpgradePurchase,
): Record<string, unknown> {
  return {
    target: cloneUpgradeTarget(purchase.target),
    enhancementLevelBefore: purchase.enhancementLevelBefore,
    ...(purchase.enhancementLevelAfter !== undefined
      ? { enhancementLevelAfter: purchase.enhancementLevelAfter }
      : {}),
  };
}

function buildMHovExecutePayload(
  beforeSnapshot: MHovExecuteBeforeSnapshot,
  afterSnapshot: HoverIdleSnapshot,
): Record<string, unknown> {
  return {
    ...toHugePayload("hovBefore", beforeSnapshot.hov),
    ...toHugePayload("hovGainPerSecondBefore", beforeSnapshot.hovGainPerSecond),
    ...toHugePayload(
      "rewardBasisHov",
      beforeSnapshot.mHov.currentLoopHovEarned,
    ),
    ...toHugePayload("mHovMultiplierBefore", beforeSnapshot.mHov.hgMultiplier),
    ...toHugePayload("mHovMultiplierAfter", afterSnapshot.mHov.hgMultiplier),
    mHovCountBefore: beforeSnapshot.mHov.count,
    mHovCountAfter: afterSnapshot.mHov.count,
  };
}

function buildNHovExecutePayload(
  branchId: NHovBranchId,
  beforeSnapshot: HoverIdleSnapshot,
  afterSnapshot: HoverIdleSnapshot,
): Record<string, unknown> {
  const branchExperienceBefore =
    beforeSnapshot.nHov.branchExperienceByBranch[branchId];
  const branchExperienceAfter =
    afterSnapshot.nHov.branchExperienceByBranch[branchId];
  const branchExperienceGained = branchExperienceAfter.sub(
    branchExperienceBefore,
  );
  const previewBefore = beforeSnapshot.nHov.rewardPreviews.find(
    (entry) => entry.branchId === branchId,
  );
  const previewAfter = afterSnapshot.nHov.rewardPreviews.find(
    (entry) => entry.branchId === branchId,
  );
  const usesContinuousProgress = previewBefore?.freshnessWallActive === true;
  const branchLevelBefore = usesContinuousProgress
    ? calculateNHovBranchContinuousLevel({ experience: branchExperienceBefore })
    : beforeSnapshot.nHov.derivedBranchLevels[branchId];
  const branchLevelAfter = usesContinuousProgress
    ? calculateNHovBranchContinuousLevel({ experience: branchExperienceAfter })
    : afterSnapshot.nHov.derivedBranchLevels[branchId];

  return {
    ...toHugePayload("hovBefore", beforeSnapshot.hov),
    ...toHugePayload("hovGainPerSecondBefore", beforeSnapshot.hovGainPerSecond),
    ...toHugePayload("runBaseHov", beforeSnapshot.mHov.currentLoopHovEarned),
    ...toHugePayload("branchExperienceGained", branchExperienceGained),
    branchLevelBefore,
    branchLevelAfter,
    ...toHugePayload("branchExperienceBefore", branchExperienceBefore),
    ...toHugePayload("branchExperienceAfter", branchExperienceAfter),
    ...(previewBefore === undefined
      ? {}
      : {
          ...toHugePayload("branchEffectBefore", previewBefore.currentEffect),
          ...toHugePayload(
            "branchEffectFreshAfter",
            previewBefore.freshNextEffect,
          ),
          ...toHugePayload(
            "branchEffectEffectiveAfter",
            previewBefore.effectiveNextEffect,
          ),
        }),
    ...(previewAfter === undefined
      ? {}
      : toHugePayload("branchEffectAfter", previewAfter.currentEffect)),
    nHovCountBefore: beforeSnapshot.nHov.count,
    nHovCountAfter: afterSnapshot.nHov.count,
  };
}

function buildOHovExecutePayload(
  beforeSnapshot: HoverIdleSnapshot,
  afterSnapshot: HoverIdleSnapshot,
): Record<string, unknown> {
  const powerGain = afterSnapshot.oHov.power.sub(beforeSnapshot.oHov.power);
  const hovCoreGain = afterSnapshot.oHov.hovCore.sub(
    beforeSnapshot.oHov.hovCore,
  );

  return {
    ...toHugePayload("hovBefore", beforeSnapshot.hov),
    ...toHugePayload("hovGainPerSecondBefore", beforeSnapshot.hovGainPerSecond),
    ...toHugePayload(
      "currentLoopHovEarned",
      beforeSnapshot.mHov.currentLoopHovEarned,
    ),
    ...toHugePayload("powerGain", powerGain),
    ...toHugePayload("hovCoreGain", hovCoreGain),
    oHovCountBefore: beforeSnapshot.oHov.count,
    oHovCountAfter: afterSnapshot.oHov.count,
    runGameDurationMs: beforeSnapshot.oHov.currentRunGameElapsedMs,
    runWallDurationMs: beforeSnapshot.oHov.currentRunElapsedMs,
  };
}

function toHugePayload(
  fieldName: string,
  value: HugeNumber,
): Record<string, string | number | null> {
  return {
    [fieldName]: serializeHuge(value),
    [`${fieldName}Log10`]: toSafeLog10(value),
  };
}

function toSafeLog10(value: HugeNumber): number | null {
  if (!value.isFinite() || compareHuge(value, 0) <= 0) {
    return null;
  }

  try {
    const result = value.log10().toNumber();
    return Number.isFinite(result) ? result : null;
  } catch {
    return null;
  }
}

function subtractNullable(
  current: number | null,
  previous: number | null,
): number | null {
  if (current === null || previous === null) {
    return null;
  }
  const delta = current - previous;
  return Number.isFinite(delta) ? delta : null;
}

function normalizeRatio(value: number): number {
  if (!Number.isFinite(value) || value <= 0) {
    return 0;
  }
  if (value >= 1) {
    return 1;
  }
  return value;
}

function resolvePlayLogTraceMode(
  purpose: string | undefined,
): PlayLogTraceMode {
  if (purpose === undefined) {
    return "normal";
  }
  const tokens = purpose.toLowerCase().split(/[^a-z0-9]+/u);
  if (tokens.includes("calibration")) {
    return "calibration";
  }
  if (tokens.includes("analysis")) {
    return "analysis";
  }
  return "normal";
}

function normalizePositiveFinite(value: number, name: string): number {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(
      `PlayLog runtime ${name} must be a positive finite number.`,
    );
  }

  return value;
}

function normalizeNonNegativeFinite(value: number, name: string): number {
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(
      `PlayLog runtime ${name} must be a non-negative finite number.`,
    );
  }

  return value;
}
